// DataSection.jsx

import React, { useState, useEffect, forwardRef, useImperativeHandle, useRef, useMemo } from 'react';
import {
    DataSectionContainer,
    TableContainer,
    TableHeader,
    StyledTable,
    TableCell,
    TableHeaderCell,
} from './DataSectionStyle';

import {
    Box,
    Typography,
    Tooltip,
    Button,
    Chip,
    Stack,
    Grid,
    Card,
    CardContent,
} from '@mui/material';
import axios from 'axios';

import { processApsData } from "../../../analysis/aps/apsFetchData";
import { validateApsData } from "../../../analysis/aps/apsValidations";
import { renderApsDataCards } from "../../../analysis/aps/apsDataCards/apsDataCards";

import { processCosechaMecanicaData } from "../../../analysis/cosechaMecanica/cosechaMecanicaFetchData";
import { validateCosechaMecanicaData } from "../../../analysis/cosechaMecanica/cosechaMecanicaValidations";
import { validateFertilizationData } from "../../../analysis/fertilizacion/fertilizacionValidations";
import { renderCosechaMecanicaDataCards } from "../../../analysis/cosechaMecanica/cosechaMecanicaDataCards";
import { validateConteoPalmasData } from "../../../analysis/conteoPalmas/conteoPalmasValidation";
import { renderConteoPalmasDataCards } from "../../../analysis/conteoPalmas/conteoPalmasDataCards";
import { validateConteoAgavesData } from "../../../analysis/conteoAgave/conteoAgaveValidation";
import { renderConteoAgaveDataCards } from "../../../analysis/conteoAgave/conteoAgaveDataCards";
import { validateDeteccionMalezaData } from '../../../analysis/deteccionMaleza/deteccionMalezaValidation';
import { renderDeteccionMalezaDataCards } from '../../../analysis/deteccionMaleza/deteccionMalezaDataCards';
import { validateConteoAgavesData as validateConteoAgavesData2 } from "../../../analysis/conteoAgave/conteoAgaveValidation";
import { processConteoAgavesData } from "../../../analysis/conteoAgave/conteoAgaveFetch";
import { processConteoPalmasData } from "../../../analysis/conteoPalmas/conteoPalmasFetch";
import { processDeteccionMalezaData } from '../../../analysis/deteccionMaleza/deteccionMalezaFetch';

import { displayValue, formatDecimalText } from "../../../utils/generalUtils";
import { API_BASE_PYTHON_SERVICE } from "../../../utils/config";

import ChartTemplates from "../../../components/ChartTemplates";
import { processFertilizacionData } from "../../../analysis/fertilizacion/fertilizacionFetchData";
import { renderFertilizacionDataCards } from "../../../analysis/fertilizacion/fertilizacionDataCards/fertilizacionDataCards";

// ECharts para capturar imágenes de gráficas
import * as echarts from 'echarts/core';

// Ícono descarga
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

// Convierte posibles objetos del Autocomplete a texto (value/label/id/codigo)
const __asTextValue = (v) => {
    if (v == null) return "";
    if (typeof v === "object") {
        return String(v.value ?? v.id ?? v.codigo ?? v.label ?? "").trim();
    }
    return String(v).trim();
};

const __normalize = (v) =>
    __asTextValue(v)
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

const __getValor = (v) => {
    if (v && typeof v === "object" && !Array.isArray(v)) {
        if (v.raw != null) return v.raw;
        if (v.valor != null) return v.valor;
    }
    return v;
};

const __getRawArray = (v) => {
    const val = __getValor(v);
    if (Array.isArray(val)) return val;
    if (val == null || val === "") return [];
    return [val];
};

const __toSet = (arr) =>
    new Set(__getRawArray(arr).map(__normalize).filter((x) => x !== ""));

const __formatSeriesValor = (arr) => {
    const a = __getRawArray(arr);
    if (!a.length) return "";
    return a
        .map((x) => __asTextValue(x))
        .filter((x) => x !== "")
        .join(", ");
};
const __normKeyLoose = (k) =>
    String(k ?? "")
        .trim()
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]/g, "");

const __getAny = (obj, keys, fallback = undefined) => {
    if (!obj || typeof obj !== "object") return fallback;

    // 1) directo
    for (const k of keys) {
        if (Object.prototype.hasOwnProperty.call(obj, k) && obj[k] != null) return obj[k];
    }

    // 2) loose map (por si viene como C_FINCAS / C. FINCA / c_fincas, etc.)
    const map = {};
    Object.keys(obj).forEach((realKey) => {
        map[__normKeyLoose(realKey)] = realKey;
    });

    for (const k of keys) {
        const real = map[__normKeyLoose(k)];
        if (real && obj[real] != null) return obj[real];
    }

    return fallback;
};

const __asArray = (v) => (Array.isArray(v) ? v : v == null ? [] : [v]);

const filterApsDatosAnalisisByLoteFilters = (datosAnalisis, loteFilters) => {
    if (!datosAnalisis || typeof datosAnalisis !== "object") return datosAnalisis;

    const f = loteFilters || {};
    const hasActive =
        (Array.isArray(f.loteId) && f.loteId.length) ||
        (Array.isArray(f.finca) && f.finca.length) ||
        (Array.isArray(f.codigoFinca) && f.codigoFinca.length) ||
        (Array.isArray(f.lote) && f.lote.length);

    if (!hasActive) return datosAnalisis;

    const listado = __getValor(datosAnalisis?.listadoIdsLoteKmls);
    const listadoArr = Array.isArray(listado) ? listado : [];
    if (!listadoArr.length) return datosAnalisis;

    const idSet = __toSet(f.loteId);
    const fincaSet = __toSet(f.finca);
    const cFincaSet = __toSet(f.codigoFinca);
    const loteSet = __toSet(f.lote);

    // 1) Determinar KMLs permitidos usando la estructura real del backend
    const allowedKml = new Set();

    for (const entry of listadoArr) {
        const kmlVal = __getAny(entry, ["KML", "kml"], "");
        const kmlKey = __normalize(kmlVal);

        const ids = __asArray(__getAny(entry, ["IDS", "ids", "IDS_LOTES", "ids_lotes"], []));
        const nFincas = __asArray(__getAny(entry, ["N_FINCAS", "n_fincas", "NFINCAS"], []));
        const cFincas = __asArray(__getAny(entry, ["C_FINCAS", "c_fincas", "CFINCAS"], []));
        const lotes = __asArray(__getAny(entry, ["LOTES", "lotes", "LOTE", "lote"], []));

        const lotesInfo = __asArray(__getAny(entry, ["LOTES_INFO", "lotes_info", "lotesInfo"], []));
        const lotesFromInfo = lotesInfo
            .map((li) => __getAny(li, ["LOTE", "lote", "NUM_LOTE", "Lote"], ""))
            .filter((x) => String(x ?? "").trim() !== "");

        let ok = true;

        if (idSet.size) {
            ok = ok && ids.some((x) => idSet.has(__normalize(x)));
        }
        if (fincaSet.size) {
            ok = ok && nFincas.some((x) => fincaSet.has(__normalize(x)));
        }
        if (cFincaSet.size) {
            ok = ok && cFincas.some((x) => cFincaSet.has(__normalize(x)));
        }
        if (loteSet.size) {
            const allLotes = [].concat(lotes || []).concat(lotesFromInfo || []);
            ok = ok && allLotes.some((x) => loteSet.has(__normalize(x)));
        }

        if (ok) allowedKml.add(kmlKey);
    }

    // Si no matchea nada, devolvemos series vacías (es lo correcto cuando el filtro no aplica)
    // Si prefieres “no filtrar” en ese caso, cambia por: `if (!allowedKml.size) return datosAnalisis;`
    if (!allowedKml.size) {
        return {
            ...datosAnalisis,
            alturas: [],
            dosisReales: [],
            dosisAreas: [],
            listadoKmls: [],
            numeroKmls: 0,
            listadoIdsLoteKmls: [],
        };
    }

    // 2) Resolver orden real de KMLs
    let kmlList = __getRawArray(datosAnalisis?.listadoKmls);
    if (!Array.isArray(kmlList) || !kmlList.length) {
        kmlList = listadoArr
            .map((e) => __getAny(e, ["KML", "kml"], ""))
            .filter((v) => String(v ?? "").trim() !== "")
            .sort((a, b) => Number(a) - Number(b));
    }

    // 3) Índices a mantener
    const keepIdx = [];
    const keepKmlList = [];

    kmlList.forEach((k, idx) => {
        const key = __normalize(k);
        const altKey = __normalize(idx + 1); // fallback por si vienen 1..n
        if (allowedKml.has(key) || allowedKml.has(altKey)) {
            keepIdx.push(idx);
            keepKmlList.push(k);
        }
    });

    const pickByIdx = (arr) => {
        if (!Array.isArray(arr)) return [];
        return keepIdx.map((i) => {
            const v = arr[i];
            if (v == null) return null;
            if (typeof v === "string" && v.trim() === "") return null;
            return v;
        });
    };

    // 4) Filtrar series por vuelo
    const alturasArr = __getRawArray(datosAnalisis?.alturas);
    const dosisRealesArr = __getRawArray(datosAnalisis?.dosisReales);
    const dosisAreasArr = __getRawArray(datosAnalisis?.dosisAreas);

    const alturasFiltradas = pickByIdx(alturasArr);
    const dosisRealesFiltradas = pickByIdx(dosisRealesArr);
    const dosisAreasFiltradas = pickByIdx(dosisAreasArr);

    // 5) Filtrar listado estructurado también.
    //    Importante: también filtramos LOTES_INFO internamente para que los nombres
    //    de fincas/lotes en la tabla de detalles reflejen exactamente los filtros
    //    (ID, N. FINCA, C. FINCA, LOTE) y/o el click en polígono.

    const hasIdFilter = idSet.size > 0;
    const hasNFincaFilter = fincaSet.size > 0;
    const hasCFincaFilter = cFincaSet.size > 0;
    const hasLoteFilter = loteSet.size > 0;

    // Filtro fino dentro de LOTES_INFO para que la tabla (nombres de fincas/lotes) refleje
    // exactamente lo filtrado por (ID / N. FINCA / C. FINCA / LOTE) y/o por click en polígono.
    // Nota: el "ID" puede venir como ID o como ID_LOTE dependiendo de la fuente; por eso se valida en ambos.
    const matchesLoteInfo = (li) => {
        if (!li || typeof li !== "object") return false;

        const idLote = __normalize(__getAny(li, ["ID_LOTE", "LOTE_ID", "idLote", "id_lote"], ""));
        const id = __normalize(__getAny(li, ["ID", "id"], ""));
        const nFinca = __normalize(__getAny(li, ["N_FINCA", "N. FINCA", "nFinca", "N_FINCAS", "n_finca"], ""));
        const cFinca = __normalize(__getAny(li, ["C_FINCA", "C. FINCA", "cFinca", "C_FINCAS", "c_finca"], ""));
        const lote = __normalize(__getAny(li, ["LOTE", "lote", "NUM_LOTE", "num_lote"], ""));

        // ID: puede corresponder a ID_LOTE (click) o a ID (filtro del toolbar)
        if (hasIdFilter) {
            const hasSomeId = Boolean(idLote) || Boolean(id);
            if (!hasSomeId) return false;
            if (!(idSet.has(idLote) || idSet.has(id))) return false;
        }

        if (hasNFincaFilter) {
            if (!nFinca) return false;
            if (!fincaSet.has(nFinca)) return false;
        }

        if (hasCFincaFilter) {
            if (!cFinca) return false;
            if (!cFincaSet.has(cFinca)) return false;
        }

        if (hasLoteFilter) {
            if (!lote) return false;
            if (!loteSet.has(lote)) return false;
        }

        return true;
    };

    const rebuildEntryFromLotesInfo = (entry, lotesInfoFiltrado) => {
        if (!Array.isArray(lotesInfoFiltrado) || lotesInfoFiltrado.length === 0) return entry;

        const uniq = (arr) => {
            const outArr = [];
            const seen = new Set();
            arr.forEach((v) => {
                const k = __normalize(v);
                if (!k) return;
                if (seen.has(k)) return;
                seen.add(k);
                outArr.push(v);
            });
            return outArr;
        };

        const idsLotes = uniq(lotesInfoFiltrado.map((li) => __getAny(li, ["ID_LOTE", "LOTE_ID"], "")).filter(Boolean));
        const ids = uniq(lotesInfoFiltrado.map((li) => __getAny(li, ["ID"], "")).filter(Boolean));
        const lotes = uniq(lotesInfoFiltrado.map((li) => __getAny(li, ["LOTE"], "")).filter(Boolean));
        const nFincas = uniq(lotesInfoFiltrado.map((li) => __getAny(li, ["N_FINCA"], "")).filter(Boolean));
        const cFincas = uniq(lotesInfoFiltrado.map((li) => __getAny(li, ["C_FINCA"], "")).filter(Boolean));
        const tercios = uniq(lotesInfoFiltrado.map((li) => __getAny(li, ["TERCIO"], "")).filter(Boolean));
        const zafras = uniq(lotesInfoFiltrado.map((li) => __getAny(li, ["ZAFRA"], "")).filter(Boolean));

        return {
            ...entry,
            LOTES_INFO: lotesInfoFiltrado,
            IDS_LOTES: idsLotes,
            IDS: ids,
            LOTES: lotes,
            N_FINCAS: nFincas,
            C_FINCAS: cFincas,
            TERCIOS: tercios,
            ZAFRAS: zafras,
        };
    };

    const listadoFiltrado = listadoArr
        .map((entry) => {
            const k = __normalize(__getAny(entry, ["KML", "kml"], ""));
            if (!allowedKml.has(k)) return null;

            const lotesInfo = Array.isArray(entry?.LOTES_INFO) ? entry.LOTES_INFO : [];

            // Si tenemos LOTES_INFO, filtramos dentro para reflejar exactamente los filtros
            if (lotesInfo.length) {
                const lotesInfoFiltrado = lotesInfo.filter(matchesLoteInfo);
                if (!lotesInfoFiltrado.length) return null;
                return rebuildEntryFromLotesInfo(entry, lotesInfoFiltrado);
            }

            // Si no hay LOTES_INFO, dejamos la entrada tal cual (ya pasó por allowedKml)
            return entry;
        })
        .filter(Boolean);

    // 6) Mantener forma del dato: si era array -> array; si era objeto {valor/raw} -> objeto
    const setMaybeCard = (key, arr, formatter) => {
        const cur = datosAnalisis?.[key];
        if (Array.isArray(cur)) return arr;
        if (cur && typeof cur === "object") {
            return { ...cur, raw: arr, valor: formatter ? formatter(arr) : arr };
        }
        return arr;
    };

    const out = { ...datosAnalisis };

    out.listadoKmls = setMaybeCard("listadoKmls", keepKmlList, (a) => (a?.length ? a.join(", ") : ""));
    out.numeroKmls = setMaybeCard("numeroKmls", keepKmlList.length, (n) => n);

    out.alturas = setMaybeCard("alturas", alturasFiltradas, __formatSeriesValor);
    out.dosisReales = setMaybeCard("dosisReales", dosisRealesFiltradas, __formatSeriesValor);
    out.dosisAreas = setMaybeCard("dosisAreas", dosisAreasFiltradas, __formatSeriesValor);

    out.listadoIdsLoteKmls = setMaybeCard("listadoIdsLoteKmls", listadoFiltrado);
    // Compat: algunos lugares usan el nombre en mayúsculas
    out.LISTA_KMLS_IDS_LOTES = listadoFiltrado;

    return out;
};

const DataSection = forwardRef(({
                                    selectedAnalysisType,
                                    promedioAltura,
                                    areaSobreAplicada,
                                    areaAplicada,
                                    areaFueraDeLote,
                                    areaSobreCalles,
                                    porcentajeVariacion,
                                    porcentajeVariacionDosis,
                                    promedioVelocidad,
                                    areaBrutaCm,
                                    eficienciaCm,
                                    promedioVelocidadCm,
                                    porcentajeAreaPilotoCm,
                                    porcentajeAreaAutoTrackerCm,
                                    porcentajeModoCortadorBaseCm,
                                    esValorValido,
                                    setIndicadores,
                                    datosAnalisis,
                                    appliedLoteFilters,
                                    activeLotes,
                                    conteoObjetos,
                                    areaNoAplicada,
                                    onAreasCalculatedLoader,
                                    dosisTotalRealAplicada,
                                    diferenciaDeAreaApsKml,
                                    selectedSidebarOption,
                                    polygonsHarvestPlanning,
                                    polygonsHerbicidesPlanning,
                                    areaTotalLote,
                                    fertilizacionShapefileUrl
                                }, ref) => {

    /* ----------------- estados planificación ----------------- */
    const [planningGeojson, setPlanningGeojson] = useState(null);
    const [loadingPlanning, setLoadingPlanning] = useState(false);

    /* ----------------- estados de gráficas ----------------- */
    const [chartLoading, setChartLoading] = useState({});
    const [chartsConfig, setChartsConfig] = useState([]);
    const [analysisData, setAnalysisData] = useState([]);
    const [availableProps, setAvailableProps] = useState([]);
    const [distinctValuesMap, setDistinctValuesMap] = useState({});

    const chartTemplatesRef = useRef(null);
    const fertilizacionChartsRef = useRef(null);
    const apsChartsRef = useRef(null);

    // ✅ Para APS: aplicar filtros del panel del mapa (ID / N. FINCA / C. FINCA / LOTE)
    //    y que las gráficas por vuelo se actualicen automáticamente.
    //    Además, si NO hay filtros del Toolbar pero el usuario selecciona lote(s) haciendo click en el mapa,
    //    también se deben filtrar las gráficas por esos lotes seleccionados.
    const apsEffectiveLoteFilters = useMemo(() => {
        if (selectedAnalysisType !== 'APLICACIONES_AEREAS') return appliedLoteFilters;

        const f = appliedLoteFilters || {};
        const hasToolbarFilters =
            (Array.isArray(f.loteId) && f.loteId.length) ||
            (Array.isArray(f.finca) && f.finca.length) ||
            (Array.isArray(f.codigoFinca) && f.codigoFinca.length) ||
            (Array.isArray(f.lote) && f.lote.length);

        if (hasToolbarFilters) return f;

        const idsFromClick = Array.isArray(activeLotes) ? activeLotes : activeLotes ? [activeLotes] : [];
        if (idsFromClick.length) {
            return {
                loteId: idsFromClick,
                finca: [],
                codigoFinca: [],
                lote: [],
            };
        }

        return f;
    }, [selectedAnalysisType, appliedLoteFilters, activeLotes]);

    const datosAnalisisApsFiltrado = useMemo(() => {
        if (selectedAnalysisType === 'HERBICIDAS') return datosAnalisis;
        if (selectedAnalysisType !== 'APLICACIONES_AEREAS') return datosAnalisis;
        return filterApsDatosAnalisisByLoteFilters(datosAnalisis, apsEffectiveLoteFilters);
    }, [selectedAnalysisType, datosAnalisis, apsEffectiveLoteFilters]);



    /* ----------------- NUEVO: props dinámicas (fertilización) ----------------- */
    const [fertCustomProps, setFertCustomProps] = useState([]);

    // Calcula la clave de análisis como en Toolbar para leer LS
    const analysisKey =
        selectedAnalysisType && selectedAnalysisType.trim() !== ''
            ? selectedAnalysisType
            : selectedSidebarOption
                ? selectedSidebarOption
                    .normalize('NFD')
                    .replace(/[\u0300-\u036f]/g, '')
                    .replace(/\s+/g, '_')
                    .toUpperCase()
                : '';
    const customPropsStorageKey = `custom_props_${analysisKey || 'GLOBAL'}`;

    const loadFertCustomProps = () => {
        if (selectedAnalysisType !== 'FERTILIZACION') {
            setFertCustomProps([]);
            return;
        }
        try {
            const raw = localStorage.getItem(customPropsStorageKey) || '[]';
            const parsed = JSON.parse(raw);
            const clean = Array.isArray(parsed)
                ? parsed
                    .filter(r => r && String(r.label || '').trim() !== '')
                    .map(r => ({
                        label: String(r.label || '').trim(),
                        value: String(r.value ?? '').trim()
                    }))
                : [];
            setFertCustomProps(clean);
        } catch {
            setFertCustomProps([]);
        }
    };

    useEffect(() => {
        loadFertCustomProps();
        const onRefresh = () => loadFertCustomProps();
        window.addEventListener('fertilizacion:rangos-actualizados', onRefresh);
        window.addEventListener('fertilizacion:props-actualizadas', onRefresh);
        const onStorage = (e) => {
            if (e.key === customPropsStorageKey) loadFertCustomProps();
        };
        window.addEventListener('storage', onStorage);
        return () => {
            window.removeEventListener('fertilizacion:rangos-actualizados', onRefresh);
            window.removeEventListener('fertilizacion:props-actualizadas', onRefresh);
            window.removeEventListener('storage', onStorage);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedAnalysisType, selectedSidebarOption]);

    /* ----------------- exportar todas las gráficas ----------------- */
    const getEChartsImages = (containerRef, selectors) => {
        const imgs = [];
        if (!containerRef.current) return imgs;
        const chartEls = containerRef.current.querySelectorAll(selectors);
        for (const [index, el] of Array.from(chartEls).entries()) {
            // ECharts instance is attached to the first child div rendered by ReactEChartsCore
            const echartsDiv = el.querySelector('div[_echarts_instance_]');
            if (echartsDiv) {
                const inst = echarts.getInstanceByDom(echartsDiv);
                if (inst) {
                    const titleNode = el.querySelector('[data-chart-title], .chart-title, h1, h2, h3, h4, h5, h6');
                    const title = titleNode?.textContent?.trim() || `Gráfica ${index + 1}`;
                    imgs.push({
                        id: el.className || `echart-${index}`,
                        title,
                        image: inst.getDataURL({ type: 'png', pixelRatio: 2, backgroundColor: '#fff' }),
                    });
                }
            }
        }
        return imgs;
    };

    useImperativeHandle(ref, () => ({
        exportAllChartsAsImages: async () => {
            const images = [];

            if (
                chartTemplatesRef.current &&
                typeof chartTemplatesRef.current.exportAllChartsAsImages === "function"
            ) {
                const templateImages =
                    await chartTemplatesRef.current.exportAllChartsAsImages();
                images.push(...templateImages);
            }

            images.push(...getEChartsImages(fertilizacionChartsRef, ".fertPieChart, .fertBarChart"));
            images.push(...getEChartsImages(apsChartsRef, ".apsPieChart, .apsBarChart, .apsLineChart, .apsHistChart"));

            return images;
        },
    }));

    /* ----------------- cargar geojson planificación ----------------- */
    useEffect(() => {
        if (!['Planificación de cosecha', 'Planificación de Herbicidas'].includes(selectedSidebarOption)) return;

        const polygonsProp =
            selectedSidebarOption === 'Planificación de cosecha'
                ? polygonsHarvestPlanning
                : polygonsHerbicidesPlanning;

        if (!polygonsProp) return;

        const loadGeojson = async (src) => {
            try {
                setLoadingPlanning(true);
                const data = await fetch(src).then(r => r.json());
                setPlanningGeojson(data);
            } catch (err) {
                console.error('Error cargando GeoJSON de planificación:', err);
            } finally {
                setLoadingPlanning(false);
            }
        };

        if (typeof polygonsProp === 'string') {
            loadGeojson(polygonsProp);
        } else if (polygonsProp?.type === 'FeatureCollection') {
            setPlanningGeojson(polygonsProp);
        }

        setIndicadores({ analisis: selectedSidebarOption });
    }, [
        selectedSidebarOption,
        polygonsHarvestPlanning,
        polygonsHerbicidesPlanning,
        setIndicadores,
    ]);

    /* ----------------- props disponibles de planificación ----------------- */
    useEffect(() => {
        if (!planningGeojson?.features?.length) return;
        setAvailableProps(Object.keys(planningGeojson.features[0].properties));
    }, [planningGeojson]);

    /* ----------------- mapa de valores distintos ----------------- */
    useEffect(() => {
        if (!planningGeojson?.features?.length) return;

        const tempMap = {};
        for (const feature of planningGeojson.features) {
            const fProps = feature.properties || {};
            for (const key of Object.keys(fProps)) {
                if (!tempMap[key]) tempMap[key] = new Set();
                tempMap[key].add(fProps[key]);
            }
        }

        const finalMap = {};
        Object.keys(tempMap).forEach(k => {
            finalMap[k] = Array.from(tempMap[k]);
        });
        setDistinctValuesMap(finalMap);
    }, [planningGeojson]);

    /* ----------------- analizar una gráfica ----------------- */
    const handleAnalyzeOneChart = async (index) => {
        if (!planningGeojson) return;
        try {
            setChartLoading(prev => ({ ...prev, [index]: true }));

            const singleChart = chartsConfig[index];
            const payload = {
                geojson: planningGeojson,
                charts: [singleChart],
            };

            const resp = await axios.post(
                `${API_BASE_PYTHON_SERVICE}mapping/chart_analysis`,
                payload
            );
            if (resp.data.status === "success") {
                const newResult = resp.data.data[0];
                setAnalysisData(prev => {
                    const updated = [...prev];
                    updated[index] = newResult;
                    return updated;
                });
                setChartsConfig(prev => {
                    const updated = [...prev];
                    updated[index].editMode = false;
                    return updated;
                });
            } else {
                console.error("Error en chart_analysis individual:", resp.data.message);
            }
        } catch (error) {
            console.error("Error /chart_analysis individual:", error);
        } finally {
            setChartLoading(prev => ({ ...prev, [index]: false }));
        }
    };

    const handleRemoveChart = (index) => {
        setChartsConfig(prev => prev.filter((_, i) => i !== index));
        setAnalysisData(prev => prev.filter((_, i) => i !== index));

        setChartLoading(prev => {
            const updated = { ...prev };
            delete updated[index];
            return updated;
        });
    };

    /* ----------------- procesar datos normales ----------------- */
    useEffect(() => {
        console.log("ESTOS SON LOS DATOS ANALISIS:", datosAnalisis);
        if (datosAnalisis && Object.keys(datosAnalisis).length > 0) {
            let datos = {};
            switch (selectedAnalysisType) {
                case 'APLICACIONES_AEREAS':
                case 'HERBICIDAS':
                    validateApsData(datosAnalisisApsFiltrado);
                    datos = processApsData(datosAnalisisApsFiltrado, {
                        promedioAltura,
                        areaSobreAplicada,
                        areaAplicada,
                        areaTotalLote,
                        areaNoAplicada,
                        areaFueraDeLote,
                        areaSobreCalles,
                        porcentajeVariacion,
                        porcentajeVariacionDosis,
                        promedioVelocidad,
                        diferenciaDeAreaApsKml,
                        dosisTotalRealAplicada
                    });
                    break;
                case 'COSECHA_MECANICA':
                    validateCosechaMecanicaData(datosAnalisis);
                    datos = processCosechaMecanicaData(datosAnalisis, {
                        areaBrutaCm,
                        eficienciaCm,
                        promedioVelocidadCm,
                        porcentajeAreaPilotoCm,
                        porcentajeAreaAutoTrackerCm,
                        porcentajeModoCortadorBaseCm,
                    });
                    break;
                case 'CONTEO_PALMA':
                    validateConteoPalmasData(datosAnalisis);
                    datos = processConteoPalmasData(datosAnalisis, {});
                    break;
                case 'CONTEO_AGAVE':
                    validateConteoAgavesData(datosAnalisis);
                    datos = processConteoAgavesData(datosAnalisis, {});
                    break;
                case 'DETECCION_MALEZA':
                    validateDeteccionMalezaData(datosAnalisis);
                    datos = processDeteccionMalezaData(datosAnalisis, {});
                    break;
                case 'FERTILIZACION':
                    validateFertilizationData(datosAnalisis);
                    datos = processFertilizacionData(datosAnalisis, {
                        promedioAltura,
                        areaSobreAplicada,
                        areaAplicada,
                        areaTotalLote,
                        areaNoAplicada,
                        areaFueraDeLote,
                        areaSobreCalles,
                        porcentajeVariacion,
                        promedioVelocidad,
                        diferenciaDeAreaApsKml,
                        dosisTotalRealAplicada
                    });
                    break;
                default:
                    datos = {};
                    break;
            }
            setIndicadores(datos);
        }
    }, [
        selectedAnalysisType,
        datosAnalisis,
        promedioAltura,
        areaSobreAplicada,
        areaAplicada,
        areaTotalLote,
        areaNoAplicada,
        areaFueraDeLote,
        areaSobreCalles,
        porcentajeVariacion,
        porcentajeVariacionDosis,
        promedioVelocidad,
        areaBrutaCm,
        eficienciaCm,
        promedioVelocidadCm,
        porcentajeAreaPilotoCm,
        porcentajeAreaAutoTrackerCm,
        porcentajeModoCortadorBaseCm,
        diferenciaDeAreaApsKml,
        dosisTotalRealAplicada,
        setIndicadores
    ]);

    /* =========================================================
       HELPERS para el Resumen por lote (UI/UX mejorada)
    ========================================================== */
    const isValidNumber = (v) =>
        v !== null && v !== undefined && !isNaN(Number(v)) && Number(v) !== 0;

    const buildGroupsByValue = (lotesArr = [], valuesArr = [], decimals = 2) => {
        const lotes = Array.isArray(lotesArr) ? lotesArr.map(String) : [];
        const values = Array.isArray(valuesArr) ? valuesArr.map(Number) : [];
        const n = Math.min(lotes.length, values.length);

        const map = new Map(); // key = valor formateado, value = array de lotes
        for (let i = 0; i < n; i++) {
            const v = values[i];
            const lote = lotes[i];
            if (!isValidNumber(v) || !lote) continue;
            const key = Number(v).toFixed(decimals);
            if (!map.has(key)) map.set(key, []);
            map.get(key).push(lote);
        }

        // Orden ascendente por valor
        return [...map.entries()]
            .sort((a, b) => parseFloat(a[0]) - parseFloat(b[0]))
            .map(([valor, lotes]) => ({ valor, lotes }));
    };

    // NUEVO: calcular media (ignorando NaN/null/0 como en isValidNumber)
    const calcAverage = (arr, decimals = 2) => {
        const nums = (Array.isArray(arr) ? arr : [])
            .map(Number)
            .filter(isValidNumber);
        if (!nums.length) return null;
        const sum = nums.reduce((a, b) => a + b, 0);
        return Number((sum / nums.length).toFixed(decimals));
    };

    const KNOWN_UNIT_PATTERN = /(kg\s*\/\s*ha|k\s*\/\s*ha|l\s*\/\s*ha|kg|kilos?|kilogramos?|litros?|\bl\b|m\s*\/\s*s|km\s*\/\s*h|\bha\b|%)/i;

    const formatWithUnit = (raw, unit) => {
        if (raw === null || raw === undefined) return "";
        const rawText = String(raw).trim();
        if (!rawText) return "";

        const val = formatDecimalText(rawText, 2);
        const existingUnitMatch = rawText.match(KNOWN_UNIT_PATTERN) || String(val).match(KNOWN_UNIT_PATTERN);
        if (existingUnitMatch) {
            const numberMatch = rawText.replace(/,/g, '.').match(/-?\d+(?:\.\d+)?/);
            const numberText = numberMatch ? formatDecimalText(numberMatch[0], 2) : val;
            return `${numberText} ${existingUnitMatch[0].replace(/\s+/g, ' ').trim()}`.trim();
        }

        const u = String(unit || "").trim();
        if (!u) return val;

        const lowerVal = String(val).toLowerCase();
        const lowerU = u.toLowerCase();

        // Si el valor ya trae una dimensional válida, no se concatena otra.
        // Evita casos como "2 Kg/ha L/ha" cuando la unidad fallback aún llega como L/ha.

        if (lowerVal.endsWith(` ${lowerU}`)) return val;

        if (lowerVal.endsWith(lowerU)) {
            const prev = lowerVal[lowerVal.length - lowerU.length - 1];
            if (prev && /[0-9.,]/.test(prev)) return val;
        }

        return `${val} ${u}`;
    };

    /** Tarjeta responsiva para una métrica por lote */
    const MetricCard = ({
                            title,
                            unit = "",
                            groups = [],
                            fallback = "No hay datos",
                            valuesOnly = false,
                            layout = "row",
                            avgValue = null,
                            avgDecimals = 2,
                            compact = false,
                        }) => {
        const hasGroups = Array.isArray(groups) && groups.length > 0;
        const avgIsValid = avgValue !== null && avgValue !== undefined && !isNaN(Number(avgValue));
        const avgFormatted = avgIsValid ? Number(avgValue).toFixed(avgDecimals) : null;

        return (
            <Card
                variant="outlined"
                sx={{
                    borderRadius: 3,
                    boxShadow: '0 2px 10px rgba(0,0,0,0.04)',
                    height: '100%',
                }}
            >
                <CardContent sx={{ p: compact ? 1 : (layout === "stacked" ? 1.5 : 2) }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', mb: compact ? 0.25 : (layout === "stacked" ? 0.5 : 0.75) }}>
                        <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 800, letterSpacing: 0.2, opacity: 0.9, fontSize: compact ? 12 : undefined }}
                        >
                            {title}
                        </Typography>
                    </Box>

                    {hasGroups ? (
                        valuesOnly ? (
                            <Stack spacing={0.25} alignItems="flex-start" justifyContent="center">
                                {groups.map((g, idx) => (
                                    <Typography
                                        key={`${title}-only-${idx}`}
                                        variant="body1"
                                        sx={{
                                            fontWeight: 500,
                                            lineHeight: 1.15,
                                            fontSize: compact ? "0.9rem" : "1rem",
                                            color: 'text.secondary'
                                        }}
                                    >
                                        {formatWithUnit(g.valor, unit)}
                                    </Typography>
                                ))}
                            </Stack>
                        ) : layout === "stacked" ? (
                            <Stack spacing={0.75}>
                                <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 500, lineHeight: 1.15, fontSize: compact ? "0.9rem" : "1rem", color: 'text.secondary' }}
                                >
                                    {formatWithUnit(groups[0].valor, unit)}
                                </Typography>
                                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                                    {groups.flatMap(g => g.lotes).map((l) => (
                                        <Chip
                                            key={`${title}-lote-${l}`}
                                            label={l}
                                            size="small"
                                            sx={{ bgcolor: 'grey.100' }}
                                        />
                                    ))}
                                </Stack>
                            </Stack>
                        ) : (
                            <Stack spacing={0.75}>
                                {groups.map((g, idx) => (
                                    <Box
                                        key={`${title}-${idx}`}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1,
                                            flexWrap: 'wrap'
                                        }}
                                    >
                                        <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                                            {g.lotes.map((l) => (
                                                <Chip
                                                    key={`${title}-lote-${l}`}
                                                    label={l}
                                                    size="small"
                                                    sx={{ bgcolor: 'grey.100' }}
                                                />
                                            ))}
                                        </Stack>

                                        <Chip
                                            size="small"
                                            variant="outlined"
                                            label={formatWithUnit(g.valor, unit)}
                                            sx={{ fontWeight: 800 }}
                                        />
                                    </Box>
                                ))}
                            </Stack>
                        )
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: valuesOnly ? 'flex-start' : 'center',
                                minHeight: compact ? 28 : 40
                            }}
                        >
                            <Typography
                                variant={valuesOnly ? "body1" : "body2"}
                                sx={{
                                    fontWeight: valuesOnly ? 500 : 700,
                                    opacity: 0.9,
                                    textAlign: valuesOnly ? 'left' : 'center',
                                    fontSize: valuesOnly ? (compact ? "0.9rem" : "1rem") : undefined,
                                    color: valuesOnly ? 'text.secondary' : undefined
                                }}
                            >
                                {fallback}
                            </Typography>
                        </Box>
                    )}

                    {avgFormatted && (
                        <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                            <Chip
                                label={avgFormatted}
                                size="small"
                                sx={{
                                    borderRadius: '999px',
                                    bgcolor: 'var(--primary-color)',
                                    color: 'common.white',
                                    fontWeight: 800,
                                    px: 1,
                                    height: 24,
                                    fontSize: 12
                                }}
                            />
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    /* ----------------- vistas planificación ----------------- */
    if (selectedSidebarOption === "Planificación de cosecha" ||
        selectedSidebarOption === "Planificación de Herbicidas") {

        return (
            <DataSectionContainer>
                <Typography variant="h4" gutterBottom>
                    {selectedSidebarOption}
                </Typography>

                {loadingPlanning && <p>Cargando GeoJSON...</p>}

                {planningGeojson && (
                    <ChartTemplates
                        ref={chartTemplatesRef}
                        availableProps={availableProps}
                        chartsConfig={chartsConfig}
                        analysisData={analysisData}
                        onChangeChartsConfig={setChartsConfig}
                        onRemoveChart={handleRemoveChart}
                        onAnalyzeOneChart={handleAnalyzeOneChart}
                        distinctValuesMap={distinctValuesMap}
                        chartLoadingStates={chartLoading}
                        harvestGeojson={planningGeojson}
                    />
                )}

                {!planningGeojson && !loadingPlanning && (
                    <p>No hay datos de GeoJSON disponibles.</p>
                )}
            </DataSectionContainer>
        );
    }

    /* ----------------- vista análisis normal ----------------- */
    // ✅ Para APS, usar el objeto filtrado para que la tabla "Detalles" refleje filtros/click.
    const datosAnalisisDetalles = selectedAnalysisType === 'APLICACIONES_AEREAS'
        ? datosAnalisisApsFiltrado
        : datosAnalisis;

    // Solicitud visual: todas las variantes de Aplicaciones Aéreas usan los
    // encabezados azul oscuro del dashboard, sin cambiar otros análisis.
    // Herbicidas comparte la presentación de Aplicaciones Aéreas.
    const isAplicacionesAereas = selectedAnalysisType === 'APLICACIONES_AEREAS' || selectedAnalysisType === 'HERBICIDAS';

    const aerialDashboardTableHeaderStyle = isAplicacionesAereas
        ? {
            position: 'relative',
            overflow: 'hidden',
            background: 'linear-gradient(135deg, #2d3748 0%, #3d4a5c 100%)',
            borderLeft: '4px solid var(--primary-color)',
            color: '#ffffff',
            boxSizing: 'border-box',
        }
        : {};

    const tableDetails = {
        responsable: displayValue(datosAnalisisDetalles?.nombreResponsable?.valor),
        fechaInicio: displayValue(datosAnalisisDetalles?.fechaInicio?.valor || datosAnalisisDetalles?.fecha?.valor),
        fechaFin: displayValue(datosAnalisisDetalles?.fechaFin?.valor),
        nombreFinca: displayValue(datosAnalisisDetalles?.nombreFinca?.valor),
        operador: displayValue(datosAnalisisDetalles?.nombreOperador?.valor || datosAnalisisDetalles?.operador?.valor),
        actividad: displayValue(datosAnalisisDetalles?.actividad?.valor),

        // ✅ APS (Aplicaciones Aéreas) - nuevos campos
        // Robustez: por si alguna versión guardó llaves alternativas (ej: volumenAguaL / dosisProducto)
        volumenAgua: displayValue(
            (datosAnalisisDetalles?.volumenAgua?.valor ??
                datosAnalisisDetalles?.volumenAguaL?.valor ??
                datosAnalisisDetalles?.volumenDeAgua?.valor) ??
            ''
        ),
        dosisProducto: displayValue(
            (datosAnalisisDetalles?.dosisProducto?.valor ??
                datosAnalisisDetalles?.dosisProductoLHa?.valor ??
                datosAnalisisDetalles?.dosisDeProducto?.valor) ??
            ''
        ),
    };

    // SOLO mostrar "Actividad" cuando es COSECHA_MECANICA
    const baseHeaders = [
        'Responsable',
        'Fecha Inicio',
        'Fecha Fin',
        'Nombre Finca',
        'Operador',
        ...(selectedAnalysisType === 'COSECHA_MECANICA' ? ['Actividad'] : []),
    ];
    const baseValues  = [
        tableDetails.responsable,
        tableDetails.fechaInicio,
        tableDetails.fechaFin,
        tableDetails.nombreFinca,
        tableDetails.operador,
        ...(selectedAnalysisType === 'COSECHA_MECANICA' ? [tableDetails.actividad] : []),
    ];

    const fertilHeaders = (selectedAnalysisType === 'FERTILIZACION' && Array.isArray(fertCustomProps) && fertCustomProps.length > 0)
        ? fertCustomProps.map(p => p.label) : [];
    const fertilValues = (selectedAnalysisType === 'FERTILIZACION' && Array.isArray(fertCustomProps) && fertCustomProps.length > 0)
        ? fertCustomProps.map(p => (p.value || '-')) : [];

    const productosAA = (isAplicacionesAereas ? (datosAnalisisDetalles?.productosLista?.valor || []) : []);
    const equiposAA   = (isAplicacionesAereas ? (datosAnalisisDetalles?.equiposLista?.valor || []) : []);

    // Definir volumenPromedioMezcla antes de su uso
    const volumenPromedioMezcla = datosAnalisisDetalles?.volumenPromedioMezcla?.valor ?? null;

    return (
        <DataSectionContainer>
            {(selectedAnalysisType !== 'CONTEO_PALMA' &&
                selectedAnalysisType !== 'CONTEO_AGAVE' &&
                selectedAnalysisType !== 'DETECCION_MALEZA' &&
                selectedSidebarOption !== "Planificación de cosecha" &&
                selectedSidebarOption !== "Planificación de Herbicidas") && (
                <TableContainer>
                    <TableHeader style={{ position: 'relative', ...aerialDashboardTableHeaderStyle }}>
                        Detalles

                        {selectedAnalysisType === 'FERTILIZACION' && fertilizacionShapefileUrl && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: 12,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                }}
                            >
                                <Tooltip title="Descargar shapefile (.zip)" arrow placement="left">
                                    <Button
                                        component="a"
                                        href={fertilizacionShapefileUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        size="small"
                                        startIcon={<FileDownloadOutlinedIcon />}
                                        sx={{
                                            bgcolor: 'common.white',
                                            color: 'var(--primary-color)',
                                            '&:hover': { bgcolor: 'grey.100' },
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            borderRadius: '999px',
                                            px: 1.25,
                                            py: 0.5,
                                            boxShadow: 'none'
                                        }}
                                    >
                                        Shapefile
                                    </Button>
                                </Tooltip>
                            </Box>
                        )}
                    </TableHeader>

                    <StyledTable>
                        <thead>
                        <tr>
                            {baseHeaders.map((h) => (<TableHeaderCell key={h}>{h}</TableHeaderCell>))}
                            {selectedAnalysisType === 'FERTILIZACION' && fertilHeaders.map((h, i) => (
                                <TableHeaderCell key={`fert-h-${i}`}>{h}</TableHeaderCell>
                            ))}
                            {isAplicacionesAereas && ['Equipos', 'Productos'].map((h, i) => (
                                <TableHeaderCell key={`aa-h-${i}`}>{h}</TableHeaderCell>
                            ))}
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            {baseValues.map((v, i) => (<TableCell key={`base-v-${i}`}>{v}</TableCell>))}
                            {selectedAnalysisType === 'FERTILIZACION' && fertilValues.map((v, i) => (
                                <TableCell key={`fert-v-${i}`}>{v}</TableCell>
                            ))}
                            {isAplicacionesAereas && [
                                (equiposAA.length > 0 ? (
                                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                        {equiposAA.map((eq, idx) => <Chip key={`eq-${idx}`} label={eq} size="small" />)}
                                    </Stack>
                                ) : '-'),
                                (productosAA.length > 0 ? (
                                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                        {productosAA.map((pr, idx) => <Chip key={`pr-${idx}`} label={pr} size="small" />)}
                                    </Stack>
                                ) : '-')
                            ].map((v, i) => <TableCell key={`aa-v-${i}`}>{v}</TableCell>)}
                        </tr>
                        </tbody>
                    </StyledTable>
                </TableContainer>
            )}

            {/* ───────────────── Resumen por lote ──────────────── */}
            {isAplicacionesAereas && (
                <TableContainer>
                    <TableHeader style={aerialDashboardTableHeaderStyle}>Resumen</TableHeader>

                    <Box sx={{ p: 2 }}>
                        {(() => {
                            const lotes = datosAnalisisDetalles?.lotesLista?.valor || [];

                            // 🔹 Unidades dinámicas que vienen del backend (con fallback)
                            const unidadVelocidad       = datosAnalisisDetalles?.unidadVelocidad?.valor       || "m/s";
                            const unidadAltura          = datosAnalisisDetalles?.unidadAltura?.valor          || "m";
                            const unidadDosisReal       = datosAnalisisDetalles?.unidadDosisReal?.valor       || "L/ha";
                            const unidadDosisTeorica    = datosAnalisisDetalles?.unidadDosisTeorica?.valor    || "L/ha";
                            const unidadVolumenMezcla   = datosAnalisisDetalles?.unidadVolumenMezcla?.valor   || "L";
                            const unidadDiferenciaDosis = datosAnalisisDetalles?.unidadDiferenciaDosis?.valor || "L/ha";

                            // 🔹 NUEVO: Volumen de agua y Dosis de producto (van en Resumen, no en Detalles)
                            const unidadVolumenAgua    = datosAnalisisDetalles?.unidadVolumenAgua?.valor    || "L";
                            const unidadDosisProducto  = datosAnalisisDetalles?.unidadDosisProducto?.valor  || "L/ha";

                            // Agrupaciones por métrica
                            const gVel     = buildGroupsByValue(lotes, datosAnalisisDetalles?.velocidadesLista?.valor, 2);
                            const gAlt     = buildGroupsByValue(lotes, datosAnalisisDetalles?.alturasLista?.valor, 2);
                            const gDosReal = buildGroupsByValue(lotes, datosAnalisisDetalles?.dosisRealesLista?.valor, 2);
                            const gDosTeo  = buildGroupsByValue(lotes, datosAnalisisDetalles?.dosisTeoricasLista?.valor, 2);
                            const gHum     = buildGroupsByValue(lotes, datosAnalisisDetalles?.humedadesLista?.valor, 1);
                            const gTCH     = buildGroupsByValue(lotes, datosAnalisisDetalles?.tchEstimadoLista?.valor, 1);

                            // Medias
                            const avgDosTeo = calcAverage(datosAnalisisDetalles?.dosisTeoricasLista?.valor, 2);
                            const avgHum    = calcAverage(datosAnalisisDetalles?.humedadesLista?.valor, 1);
                            const avgTCH    = calcAverage(datosAnalisisDetalles?.tchEstimadoLista?.valor, 1);

                            // Fallbacks individuales
                            const fbVel     = datosAnalisisDetalles?.velocidadAps?.valor || "No hay datos";
                            const fbAlt     = datosAnalisisDetalles?.alturaAps?.valor || "No hay datos";
                            const fbDosReal = datosAnalisisDetalles?.dosisReal?.valor ? displayValue(datosAnalisisDetalles.dosisReal.valor) : "No hay datos";
                            const fbDosTeo  = (datosAnalisisDetalles?.dosisTeorica?.valor ?? '') !== '' ? `${datosAnalisisDetalles.dosisTeorica.valor}` : "No hay datos";
                            const fbHum     = datosAnalisisDetalles?.humedadDelCultivo?.valor || "No hay datos";
                            const fbTchVal  = datosAnalisisDetalles?.tchEstimado?.valor;
                            const fbTCH     = fbTchVal ? `${fbTchVal}/ha` : "No hay datos";

                            // NUEVO: valores globales (pueden venir con/sin unidad)
                            const rawVolumenAgua =
                                (datosAnalisisDetalles?.volumenAgua?.valor ??
                                    datosAnalisisDetalles?.volumenAguaL?.valor ??
                                    datosAnalisisDetalles?.volumenDeAgua?.valor) ??
                                '';
                            const rawDosisProducto =
                                (datosAnalisisDetalles?.dosisProducto?.valor ??
                                    datosAnalisisDetalles?.dosisProductoLHa?.valor ??
                                    datosAnalisisDetalles?.dosisDeProducto?.valor) ??
                                '';

                            const txtVolumenAgua = String(displayValue(rawVolumenAgua)).trim();
                            const txtDosisProducto = String(displayValue(rawDosisProducto)).trim();

                            const fbVolumenAgua =
                                txtVolumenAgua && txtVolumenAgua !== "-"
                                    ? formatWithUnit(txtVolumenAgua, unidadVolumenAgua)
                                    : "No hay datos";
                            const fbDosisProducto =
                                txtDosisProducto && txtDosisProducto !== "-"
                                    ? formatWithUnit(txtDosisProducto, unidadDosisProducto)
                                    : "No hay datos";

                            // Extras globales
                            const fbVolumenMezcla =
                                volumenPromedioMezcla !== undefined &&
                                volumenPromedioMezcla !== null &&
                                Number(volumenPromedioMezcla) > 0
                                    ? formatWithUnit(displayValue(volumenPromedioMezcla), unidadVolumenMezcla)
                                    : datosAnalisisDetalles?.volumenPromedio?.valor
                                        ? formatWithUnit(displayValue(datosAnalisisDetalles.volumenPromedio.valor), unidadVolumenMezcla)
                                        : "No hay datos";

                            const fbDiferenciaDosis = (typeof datosAnalisisDetalles?.diferenciaDosisTeoricaReal?.valor !== "undefined" && datosAnalisisDetalles?.diferenciaDosisTeoricaReal?.valor !== null)
                                ? `${displayValue(datosAnalisisDetalles?.diferenciaDosisTeoricaReal?.valor)}`
                                : "No hay datos";
                            // Tiempo total:
                            // - Al subir plantilla suele venir ya formateado como H:MM.
                            // - Al ingresar datos manualmente, algunas fuentes lo devuelven como horas decimales
                            //   (ej. 1.5) o incluso vacío/0. Aquí normalizamos SIEMPRE a H:MM.
                            // - Si no viene tiempoTotal, lo calculamos desde horaInicio/horaFin.
                            const fbTiempoTotal = (() => {
                                const pad2 = (n) => String(Math.trunc(n)).padStart(2, '0');

                                const formatMinutesAsHMM = (totalMinutes) => {
                                    const n = Number(totalMinutes);
                                    if (!Number.isFinite(n)) return null;
                                    const min = Math.max(0, Math.round(n));
                                    const hh = Math.floor(min / 60);
                                    const mm = min % 60;
                                    return `${hh}:${pad2(mm)}`;
                                };

                                const parseHMMToMinutes = (t) => {
                                    const s = String(t ?? '').trim();
                                    if (!s) return null;
                                    // soporta H:MM, HH:MM y HH:MM:SS
                                    const m = s.match(/^(\d{1,3}):(\d{2})(?::(\d{2}))?$/);
                                    if (!m) return null;
                                    const hh = Number(m[1]);
                                    const mm = Number(m[2]);
                                    const ss = m[3] != null ? Number(m[3]) : 0;
                                    if (Number.isNaN(hh) || Number.isNaN(mm) || Number.isNaN(ss)) return null;
                                    let minutes = (hh * 60) + mm;
                                    // redondeo simple por segundos
                                    if (ss >= 30) minutes += 1;
                                    return minutes;
                                };

                                const parseNumericToMinutes = (v) => {
                                    if (v === null || v === undefined) return null;
                                    let s = String(v).trim();
                                    if (!s) return null;

                                    // 1) Si viene como tiempo (con :) lo parseamos
                                    if (s.includes(':')) return parseHMMToMinutes(s);

                                    // 2) Normalizar: "1,5" => "1.5" y remover unidades (h, hrs, etc.)
                                    s = s.replace(/,/g, '.').replace(/[^0-9.\-]/g, '');
                                    if (!s || s === '-' || s === '.' || s === '-.') return null;

                                    const n = Number(s);
                                    if (!Number.isFinite(n)) return null;

                                    const abs = Math.abs(n);
                                    const hasDecimal = Math.abs(n - Math.trunc(n)) > 1e-9;

                                    // Heurística:
                                    // - Enteros grandes (>=60 y <= 1440) son más probablemente MINUTOS.
                                    // - Con decimales o valores pequeños -> HORAS.
                                    if (!hasDecimal && abs >= 60 && abs <= 24 * 60) return n; // minutos
                                    return n * 60; // horas -> minutos
                                };

                                // 1) Si viene del backend, normalizar a H:MM (soporta H:MM, horas decimales, minutos)
                                const raw = datosAnalisisDetalles?.tiempoTotal?.valor ?? datosAnalisisDetalles?.tiempoTotal;
                                const hasRaw = !(raw === undefined || raw === null || String(raw).trim() === '');
                                if (hasRaw) {
                                    const mins = parseNumericToMinutes(raw);
                                    const fmt = formatMinutesAsHMM(mins);
                                    if (fmt) return fmt;
                                }

                                // 2) Fallback desde horaInicio/horaFin (manual)
                                const hIni = datosAnalisisDetalles?.horaInicio?.valor ?? datosAnalisisDetalles?.horaInicio;
                                const hFin = datosAnalisisDetalles?.horaFin?.valor ?? datosAnalisisDetalles?.horaFin;
                                const mIni = parseHMMToMinutes(hIni);
                                const mFin = parseHMMToMinutes(hFin);
                                if (mIni === null || mFin === null) return 'No hay datos';

                                let diff = mFin - mIni;
                                if (diff < 0) diff += 24 * 60; // cruza medianoche

                                return formatMinutesAsHMM(diff) || 'No hay datos';
                            })();

                            // ── PRIMERA FILA compacta: 9 tarjetas en una sola línea (md/lg/xl)
                            const topCards = [
                                {
                                    title: "Velocidad",
                                    unit: unidadVelocidad,
                                    groups: gVel,
                                    fallback: fbVel,
                                    valuesOnly: true,
                                    layout: "stacked"
                                },
                                {
                                    title: "Altura",
                                    unit: unidadAltura,
                                    groups: gAlt,
                                    fallback: fbAlt,
                                    valuesOnly: true,
                                    layout: "stacked"
                                },
                                {
                                    title: "Volumen de agua",
                                    unit: unidadVolumenAgua,
                                    groups: [],
                                    fallback: fbVolumenAgua,
                                    valuesOnly: true,
                                    layout: "stacked"
                                },
                                {
                                    title: "Dosis de producto",
                                    unit: unidadDosisProducto,
                                    groups: [],
                                    fallback: fbDosisProducto,
                                    valuesOnly: true,
                                    layout: "stacked"
                                },
                                {
                                    title: "Volumen real",
                                    unit: unidadDosisReal,
                                    groups: gDosReal,
                                    fallback: fbDosReal,
                                    valuesOnly: true,
                                    layout: "stacked"
                                },
                                {
                                    title: "Volumen teórico",
                                    unit: unidadDosisTeorica,
                                    groups: [],
                                    fallback: fbDosTeo,
                                    valuesOnly: true,
                                    layout: "stacked"
                                },
                                {
                                    title: "Volumen mezcla",
                                    unit: unidadVolumenMezcla,
                                    groups: [],
                                    fallback: fbVolumenMezcla,
                                    valuesOnly: true,
                                    layout: "stacked"
                                },
                                {
                                    title: "Dif. volumen T vs R",
                                    unit: unidadDiferenciaDosis,
                                    groups: [],
                                    fallback: fbDiferenciaDosis,
                                    valuesOnly: true,
                                    layout: "stacked"
                                },
                                {
                                    title: "Tiempo total",
                                    unit: "",
                                    groups: [],
                                    fallback: fbTiempoTotal,
                                    valuesOnly: true,
                                    layout: "stacked"
                                },
                            ];

                            // ── SEGUNDA FILA (por lote + media)
                            const bottomCards = [
                                {
                                    title: "Volumen teorico por lote",
                                    unit: unidadDosisTeorica,
                                    groups: gDosTeo,
                                    fallback: fbDosTeo,
                                    layout: "row",
                                    avgValue: avgDosTeo,
                                    avgDecimals: 2
                                },
                                { title: "Humedad del cultivo por lote", unit: "%",    groups: gHum, fallback: fbHum, layout: "row", avgValue: avgHum, avgDecimals: 1 },
                                { title: "TCH por lote",                 unit: "/ha",  groups: gTCH, fallback: fbTCH, layout: "row", avgValue: avgTCH, avgDecimals: 1 },
                            ];

                            return (
                                <>
                                    {/* Primera fila: 9 tarjetas compactas en una sola línea a partir de md */}
                                    <Grid
                                        container
                                        spacing={1}
                                        columns={{ xs: 12, sm: 12, md: 18, lg: 18, xl: 18 }}
                                        sx={{ mb: 1, justifyContent: 'center' }}
                                    >
                                        {topCards.map((c, idx) => (
                                            <Grid
                                                key={`metric-top-${idx}`}
                                                item
                                                xs={12}
                                                sm={6}
                                                md={2}
                                                lg={2}
                                                xl={2}
                                            >
                                                <MetricCard
                                                    title={c.title}
                                                    unit={c.unit}
                                                    groups={c.groups}
                                                    fallback={c.fallback}
                                                    valuesOnly={!!c.valuesOnly}
                                                    layout={c.layout}
                                                    compact
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>

                                    {/* Segunda fila: por lote + media */}
                                    <Grid container spacing={1.25}>
                                        {bottomCards.map((c, idx) => (
                                            <Grid
                                                key={`metric-bottom-${idx}`}
                                                item
                                                xs={12} sm={6} md={4} lg={4} xl={4}
                                            >
                                                <MetricCard
                                                    title={c.title}
                                                    unit={c.unit}
                                                    groups={c.groups}
                                                    fallback={c.fallback}
                                                    layout={c.layout}
                                                    avgValue={c.avgValue}
                                                    avgDecimals={c.avgDecimals}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                </>
                            );
                        })()}
                    </Box>
                </TableContainer>
            )}

            {/* Render: solo charts (las tarjetas fueron eliminadas del módulo APS) */}
            {(selectedAnalysisType === 'APLICACIONES_AEREAS' || selectedAnalysisType === 'HERBICIDAS') &&
                renderApsDataCards(
                    validateApsData(datosAnalisisApsFiltrado),
                    datosAnalisisApsFiltrado,
                    {
                        promedioAltura,
                        areaSobreAplicada,
                        areaAplicada,
                        areaNoAplicada,
                        areaTotalLote,
                        areaFueraDeLote,
                        areaSobreCalles,
                        porcentajeVariacion,
                        porcentajeVariacionDosis,
                        promedioVelocidad,
                        diferenciaDeAreaApsKml,
                        dosisTotalRealAplicada
                    },
                    onAreasCalculatedLoader,
                    apsChartsRef
                )
            }

            {selectedAnalysisType === 'COSECHA_MECANICA' &&
                renderCosechaMecanicaDataCards(
                    validateCosechaMecanicaData(datosAnalisis),
                    datosAnalisis,
                    {
                        areaBrutaCm,
                        eficienciaCm,
                        promedioVelocidadCm,
                        porcentajeAreaPilotoCm,
                        porcentajeAreaAutoTrackerCm,
                        porcentajeModoCortadorBaseCm,
                    }
                )
            }

            {selectedAnalysisType === 'FERTILIZACION' &&
                renderFertilizacionDataCards(
                    validateFertilizationData(datosAnalisis),
                    datosAnalisis,
                    {
                        promedioAltura,
                        areaSobreAplicada,
                        areaAplicada,
                        areaTotalLote,
                        areaNoAplicada,
                        areaFueraDeLote,
                        areaSobreCalles,
                        porcentajeVariacion,
                        promedioVelocidad,
                        diferenciaDeAreaApsKml,
                        dosisTotalRealAplicada
                    },
                    onAreasCalculatedLoader,
                    fertilizacionChartsRef
                )
            }

            {selectedAnalysisType === 'CONTEO_PALMA' &&
                renderConteoPalmasDataCards(
                    validateConteoPalmasData(datosAnalisis),
                    datosAnalisis,
                    { conteoObjetos }
                )
            }

            {selectedAnalysisType === 'CONTEO_AGAVE' &&
                renderConteoAgaveDataCards(
                    validateConteoAgavesData2(datosAnalisis),
                    datosAnalisis,
                    { conteoObjetos }
                )
            }

            {selectedAnalysisType === 'DETECCION_MALEZA' &&
                renderDeteccionMalezaDataCards(
                    validateDeteccionMalezaData(datosAnalisis),
                    datosAnalisis,
                    { conteoObjetos }
                )
            }
        </DataSectionContainer>
    );
});

export default DataSection;
